import type { PortalApproval, PortalOverview, PortalPartner } from "./contracts";

function csrfToken(): string {
  const match = document.cookie.match(/(?:^|; )cg_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : "";
}

async function portalRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method || "GET").toUpperCase();
  const headers = new Headers(init.headers);
  if (method !== "GET" && method !== "HEAD") {
    headers.set("x-csrf-token", csrfToken());
    if (init.body) headers.set("content-type", "application/json");
  }
  const response = await fetch(`/app/api${path}`, {
    ...init,
    headers,
    credentials: "same-origin",
    redirect: "error"
  });
  const body = await response.json().catch(() => ({})) as T & { error?: string };
  // Only 4xx bodies carry copy written for customers; a 5xx body repeats an internal error string, which must never reach the screen.
  const failure = response.status >= 400 && response.status < 500 && body.error ? body.error : "ClearGuard could not complete that request.";
  if (!response.ok) throw Object.assign(new Error(failure), { status: response.status });
  return body;
}

export const portalAPI = {
  session: () => portalRequest<{ authenticated: true; displayName: string | null }>("/session"),
  overview: () => portalRequest<PortalOverview>("/overview"),
  startEmail: (email: string) => portalRequest<{ ok: true }>("/auth/email/start", {
    method: "POST",
    body: JSON.stringify({ email })
  }),
  completeEmail: (token: string) => portalRequest<{ ok: true }>("/auth/email/complete", {
    method: "POST",
    body: JSON.stringify({ token })
  }),
  bridgeChallenge: () => portalRequest<{ bridgeToken: string; expiresAt: string }>("/bridge/challenge", { method: "POST" }),
  partners: () => portalRequest<{ partners: PortalPartner[] }>("/partners"),
  invitePartner: (email?: string) => portalRequest<{ id: string; status: "pending"; inviteCode: string; emailSent: boolean }>("/partners/invite", {
    method: "POST",
    body: JSON.stringify(email ? { email } : {})
  }),
  acceptPartner: (inviteCode: string) => portalRequest<{ ok: true; id: string }>("/partners/accept", {
    method: "POST",
    body: JSON.stringify({ inviteCode })
  }),
  sendPartnerSupport: () => portalRequest<{ ok: true }>("/partners/support-message", { method: "POST" }),
  requestPartnerRemoval: (id: string) => portalRequest<{ id: string; status: "pending" }>(`/partners/${encodeURIComponent(id)}/removal-request`, { method: "POST" }),
  removePartner: (id: string, approvalID?: string) => portalRequest<{ ok: true }>(`/partners/${encodeURIComponent(id)}`, {
    method: "DELETE",
    body: JSON.stringify(approvalID ? { approvalID } : {})
  }),
  approvals: () => portalRequest<{ approvals: PortalApproval[] }>("/approvals"),
  resolveApproval: (id: string, resolution: "approved" | "denied") => portalRequest<{ ok: true }>(`/approvals/${encodeURIComponent(id)}/resolve`, {
    method: "POST",
    body: JSON.stringify({ resolution })
  }),
  checkout: () => portalRequest<{ checkoutURL: string }>("/billing/stripe/checkout", { method: "POST" }),
  billingPortal: () => portalRequest<{ portalURL: string }>("/billing/stripe/portal", { method: "POST" }),
  deleteAccount: (confirmation: string, storeBillingAcknowledged: boolean) => portalRequest<{ ok: true }>("/account", {
    method: "DELETE",
    body: JSON.stringify({ confirmation, storeBillingAcknowledged })
  }),
  logout: () => portalRequest<{ ok: true }>("/logout", { method: "POST" })
};
